(() => {
  const strings = window.NgCentralSolarStrings;
  const root = window.Shopify?.routes?.root || '/';
  const declarationVersion = '2026-10-14';
  const choice = { eligible: null, terms: null };
  let busy = false;
  let scheduled = false;
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
    style: 'currency', currency: window.Shopify?.currency?.active || 'EUR'
  }).format(cents / 100);
  const taxOption = name => /^(mehrwertsteuer|umsatzsteuer|mwst\.?|vat|tax)$/i.test(name.trim());
  const taxRate = value => {
    const match = /^(0|19)\s*%/.exec(value.trim());
    return match ? Number(match[1]) : null;
  };
  function groupsFor(product) {
    const groups = new Map();
    const taxIndex = product.options.findIndex(taxOption);
    for (const variant of product.variants) {
      const values = variant.options.filter((_, index) => index !== taxIndex);
      const key = JSON.stringify(values);
      if (!groups.has(key)) groups.set(key, { key, title: values.filter(value => value !== 'Default Title').join(' / '), variants: [] });
      const group = groups.get(key);
      group.variants.push(variant);
      const rate = taxIndex < 0 ? (variant.taxable ? 19 : 0) : taxRate(variant.options[taxIndex]);
      if (rate === 0 && variant.taxable === false) group.zero = variant;
      if (rate === 19 && variant.taxable === true) group.standard = variant;
    }
    return [...groups.values()];
  }
  function pricesFor(group, includesTax) {
    const zero = group.zero?.price ?? Math.round(group.standard.price / (includesTax ? 1.19 : 1));
    const standard = group.standard ? Math.round(group.standard.price * (includesTax ? 1 : 1.19)) : Math.round(zero * 1.19);
    return { zero, standard };
  }
  function buildPlan(cart, products, eligible) {
    const byId = new Map(products.map(product => [Number(product.id), product]));
    return cart.items.flatMap(item => {
      const product = byId.get(Number(item.product_id));
      if (!product) throw new Error(strings.cart_changed);
      if (!product.solar) return [];
      const group = groupsFor(product).find(group => group.variants.some(variant => Number(variant.id) === Number(item.variant_id)));
      const target = eligible ? group?.zero : group?.standard;
      if (!target) throw new Error(strings.tax_unavailable.replace('[product]', item.product_title || product.title));
      if (!target.available) throw new Error(strings.variant_unavailable.replace('[product]', item.product_title || product.title));
      if (Number(target.id) !== Number(item.variant_id) && item.selling_plan_allocation) throw new Error(strings.error);
      return [{ item, target }];
    });
  }
  async function request(path, data) {
    const response = await fetch(root + path, {
      method: data ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(data ? { body: JSON.stringify(data) } : {})
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || strings.error);
    return result;
  }
  async function freshCartData() {
    const response = await fetch(root + 'cart', { headers: { Accept: 'text/html' }, cache: 'no-store' });
    if (!response.ok) throw new Error(strings.error);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = html.querySelector('[data-ng-cart-data]');
    if (!data) throw new Error(strings.cart_changed);
    return JSON.parse(data.textContent);
  }
  function controls() {
    const element = document.querySelector('ng-central-solar-cart');
    if (!element) return null;
    const eligible = element.querySelector('[data-eligible]');
    const terms = element.querySelector('[data-terms]');
    if (!eligible || !terms) return null;
    if (choice.eligible === null) choice.eligible = eligible.checked;
    if (choice.terms === null) choice.terms = terms.checked;
    return { element, eligible, terms, status: element.querySelector('[data-status]') };
  }
  function reconcile() {
    scheduled = false;
    const current = controls();
    if (!current) return;
    current.eligible.checked = choice.eligible;
    current.terms.checked = choice.terms;
    const enabled = current.terms.checked && !busy;
    document.querySelectorAll('[name="checkout"]').forEach(button => {
      if (button.disabled === enabled) button.disabled = !enabled;
    });
    current.eligible.disabled = busy;
    current.terms.disabled = busy;
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(reconcile);
  }
  async function sync() {
    const current = controls();
    if (!current || busy || !current.terms.checked) return;
    const eligible = current.eligible.checked;
    busy = true;
    reconcile();
    current.status.textContent = strings.checking;
    try {
      const data = await freshCartData();
      let cart = await request('cart.js');
      const snapshot = items => JSON.stringify(items.map(item => [item.key, item.quantity]));
      if (snapshot(data.items) !== snapshot(cart.items)) throw new Error(strings.cart_changed);
      const plan = buildPlan(cart, data.products, eligible);
      await request('cart/update.js', { attributes: { 'PV-Nullsteuersatz': 'in Bearbeitung', 'AGB-Zustimmung': '', 'PV-Erklärung': '', 'PV-Erklärungszeitpunkt': '', 'PV-Erklärungsversion': declarationVersion } });
      for (const { item, target } of plan) {
        const properties = { ...(item.properties || {}), 'Bestätigung Nullsteuersatz': eligible ? strings.record : strings.not_confirmed };
        if (Number(item.variant_id) === Number(target.id)) {
          await request('cart/change.js', { id: item.key, quantity: item.quantity, properties });
        } else {
          await request('cart/add.js', { items: [{ id: target.id, quantity: item.quantity, properties }] });
          try {
            await request('cart/change.js', { id: item.key, quantity: 0 });
          } catch (error) {
            const added = await request('cart.js');
            const originalStillPresent = added.items.some(line => line.key === item.key);
            if (originalStillPresent) {
              const replacement = added.items.find(line => Number(line.variant_id) === Number(target.id) && JSON.stringify(line.properties) === JSON.stringify(properties));
              if (replacement) await request('cart/change.js', { id: replacement.key, quantity: Math.max(0, replacement.quantity - item.quantity) });
            }
            throw error;
          }
        }
      }
      cart = await request('cart.js');
      const verified = buildPlan(cart, data.products, eligible);
      if (cart.item_count !== data.itemCount || verified.some(({ item, target }) => Number(item.variant_id) !== Number(target.id) || item.properties?.['Bestätigung Nullsteuersatz'] !== (eligible ? strings.record : strings.not_confirmed))) throw new Error(strings.cart_changed);
      await request('cart/update.js', { attributes: { 'PV-Nullsteuersatz': eligible ? 'bestätigt' : 'nicht bestätigt', 'AGB-Zustimmung': 'bestätigt', 'PV-Erklärung': eligible ? strings.record : '', 'PV-Erklärungszeitpunkt': new Date().toISOString(), 'PV-Erklärungsversion': declarationVersion } });
      location.assign(root + 'checkout');
    } catch (error) {
      const latest = controls();
      if (latest) latest.status.textContent = error.message || strings.error;
      busy = false;
      reconcile();
    }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-show-image]');
    const gallery = button?.closest('ng-solar-product');
    if (!gallery) return;
    gallery.querySelectorAll('[data-image]').forEach(image => { image.hidden = image.dataset.image !== button.dataset.showImage; });
    gallery.querySelectorAll('[data-show-image]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
  });
  if (!customElements.get('ng-central-solar-product')) customElements.define('ng-central-solar-product', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const product = JSON.parse(this.querySelector('[data-ng-product-data]').textContent);
      const groups = groupsFor(product);
      const select = this.querySelector('select');
      for (const group of groups) {
        const preferred = group.zero?.available ? group.zero : group.standard?.available ? group.standard : group.zero || group.standard;
        if (!preferred) continue;
        const option = document.createElement('option');
        option.value = String(preferred.id);
        option.textContent = group.title;
        option.disabled = !preferred.available;
        const prices = pricesFor(group, product.includesTax);
        option.dataset.zero = String(prices.zero);
        option.dataset.standard = String(prices.standard);
        select.append(option);
      }
      select.closest('label').hidden = select.options.length < 2;
      const requested = new URL(location.href).searchParams.get('variant');
      const group = groups.find(group => group.variants.some(variant => String(variant.id) === requested));
      if (group) {
        const available = group.zero?.available ? group.zero : group.standard?.available ? group.standard : group.zero || group.standard;
        if (available) select.value = String(available.id);
      } else {
        const first = [...select.options].find(option => !option.disabled);
        if (first) select.value = first.value;
      }
      const update = () => {
        const option = select.selectedOptions[0];
        this.querySelector('[data-zero-price]').textContent = option ? money(Number(option.dataset.zero)) : strings.unavailable;
        this.querySelector('[data-standard-price]').textContent = option ? money(Number(option.dataset.standard)) : '';
        this.querySelector('button[type="submit"]').disabled = !option || option.disabled;
      };
      select.addEventListener('change', update);
      update();
      this.querySelector('form').addEventListener('submit', event => {
        if (!event.target.reportValidity() || this.querySelector('button[type="submit"]').disabled) event.preventDefault();
      });
    }
  });
  if (!customElements.get('ng-central-solar-cart')) customElements.define('ng-central-solar-cart', class extends HTMLElement {
    connectedCallback() { schedule(); }
  });
  document.addEventListener('change', event => {
    if (!event.target.closest('ng-central-solar-cart') || !event.target.matches('[data-eligible],[data-terms]')) return;
    if (event.target.matches('[data-eligible]')) choice.eligible = event.target.checked;
    if (event.target.matches('[data-terms]')) choice.terms = event.target.checked;
    const current = controls();
    if (!current) return;
    current.status.textContent = '';
    reconcile();
  });
  const guard = event => {
    if (!controls()) return;
    const checkout = event.type === 'click' ? event.target.closest('[name="checkout"],a[href="/checkout"],a[href="' + root + 'checkout"]') : event.submitter?.name === 'checkout';
    if (!checkout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    sync();
  };
  document.addEventListener('click', guard, true);
  document.addEventListener('submit', guard, true);
  document.addEventListener('cart:update', schedule);
  new MutationObserver(records => {
    if (records.some(record => record.type === 'childList' || record.target.matches?.('[name="checkout"]'))) schedule();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  schedule();
})();
